import { FormEvent, MouseEvent, useCallback, useState } from 'react';
import { Dialog as Primitive } from 'radix-ui';

import { Button, Dialog, DialogState } from '@components/primitives';

import { DialogBody } from './DialogBody';
import { DialogFooter } from './DialogFooter';

import css from './Dialog.module.css';

export type DialogConfirmationProps = {
  state: DialogState;
  title: string;
  label?: string;
  trigger?: React.ReactNode;
  children: React.ReactNode;
  variant?: 'primary' | 'danger';
  disabled?: boolean;
  onConfirm: () => void | Promise<void>;
};

export function DialogConfirmation({
  state,
  title,
  children,
  label,
  trigger,
  variant,
  onConfirm,
  disabled,
}: DialogConfirmationProps) {
  const [loading, setLoading] = useState(false);

  const handleConfirm = useCallback(
    async (
      event: FormEvent<HTMLFormElement> | MouseEvent<HTMLButtonElement>
    ) => {
      event.preventDefault();
      event.stopPropagation();

      if (disabled) return;

      const maybePromise = onConfirm();
      if (maybePromise instanceof Promise) {
        try {
          setLoading(true);
          await maybePromise;
          state.close();
        } finally {
          setLoading(false);
        }
      } else {
        state.close();
      }
    },
    [onConfirm, state.close, disabled]
  );

  return (
    <Dialog trigger={trigger} state={state} className={css.confirmation}>
      <DialogBody className={css.confirmationBody}>
        <Primitive.Title asChild>
          <h2 className={css.confirmationTitle}>{title || 'Are you sure?'}</h2>
        </Primitive.Title>
        <form id="confirmation-form" onSubmit={handleConfirm}>
          {children}
        </form>
      </DialogBody>

      <DialogFooter>
        <Button
          ghost
          disabled={loading}
          onClick={state.close}
          data-id="dialog-close"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          form="confirmation-form"
          variant={variant === 'primary' ? 'primary' : 'danger-strong'}
          loading={loading}
          onClick={handleConfirm}
          disabled={disabled}
        >
          {label || 'Confirm'}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
